import React, { useState, useEffect } from 'react';
import { linkify } from '../../utils/linkify.tsx';

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  maxAttendees: number;
  currentAttendees: number;
  rsvpEnabled: boolean;
  rsvpDeadline?: string;
  color: string;
  createdBy: string;
  createdAt: string;
  rsvps: any[];
}

interface CreateEventFormData {
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  maxAttendees: number;
  rsvpEnabled: boolean;
  rsvpDeadline: string;
  color: string;
}

// Helper function to format date string without timezone conversion
const formatDateSafe = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
};

export default function EventManagement() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAllEventsModal, setShowAllEventsModal] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState<Event[]>([]);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [viewingEvent, setViewingEvent] = useState<Event | null>(null);
  const [viewingRSVPEvent, setViewingRSVPEvent] = useState<Event | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeTBD, setTimeTBD] = useState(false);
  const [locationTBD, setLocationTBD] = useState(false);
  const [formData, setFormData] = useState<CreateEventFormData>({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    maxAttendees: 20,
    rsvpEnabled: true,
    rsvpDeadline: '',
    color: '#16a34a'
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/events', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setEvents(data.data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const eventData = {
      ...formData,
      time: timeTBD ? 'TBD' : formData.time,
      location: locationTBD ? 'TBD' : formData.location,
      rsvpDeadline: formData.rsvpEnabled ? formData.rsvpDeadline : null
    };
    

    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/events', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventData)
      });

      const data = await response.json();
      if (data.success) {
        // Add the new event to the events list
        setEvents(prevEvents => [...prevEvents, data.data]);
        closeCreateForm();
        alert('Event created successfully!');
      } else {
        alert('Error creating event: ' + data.message);
      }
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Error creating event');
    }
  };

  const handleUpdateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEvent) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/events/${editingEvent.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          time: timeTBD ? 'TBD' : formData.time,
          location: locationTBD ? 'TBD' : formData.location,
          rsvpDeadline: formData.rsvpEnabled ? formData.rsvpDeadline : null
        })
      });

      const data = await response.json();
      if (data.success) {
        // Update the specific event in the events list
        setEvents(prevEvents => prevEvents.map(event => 
          event.id === editingEvent.id ? data.data : event
        ));
        closeEditForm();
        alert('Event updated successfully!');
      } else {
        alert('Error updating event: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Error updating event');
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        // Remove the deleted event from the events list
        setEvents(prevEvents => prevEvents.filter(event => event.id !== eventId));
        alert('Event deleted successfully!');
      } else {
        alert('Error deleting event: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Error deleting event');
    }
  };

  const openViewModal = (event: Event) => {
    setViewingEvent(event);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingEvent(null);
  };

  const openEditForm = (event: Event) => {
    closeViewModal();
    setEditingEvent(event);
    setTimeTBD(event.time === 'TBD');
    setLocationTBD(event.location === 'TBD');
    setFormData({
      title: event.title,
      description: event.description,
      date: event.date,
      time: event.time === 'TBD' ? '' : event.time,
      location: event.location === 'TBD' ? '' : event.location,
      maxAttendees: event.maxAttendees,
      rsvpEnabled: event.rsvpEnabled,
      rsvpDeadline: event.rsvpDeadline || '',
      color: event.color || '#16a34a'
    });
    setShowEditForm(true);
  };

  const closeCreateForm = () => {
    setShowCreateForm(false);
    setSelectedDate(null);
    setTimeTBD(false);
    setLocationTBD(false);
    setFormData({
      title: '',
      description: '',
      date: '',
      time: '',
      location: '',
      maxAttendees: 20,
      rsvpEnabled: true,
      rsvpDeadline: '',
      color: '#16a34a'
    });
  };

  const closeEditForm = () => {
    setShowEditForm(false);
    setEditingEvent(null);
    setTimeTBD(false);
    setLocationTBD(false);
    setFormData({
      title: '',
      description: '',
      date: '',
      time: '',
      location: '',
      maxAttendees: 20,
      rsvpEnabled: true,
      rsvpDeadline: '',
      color: '#16a34a'
    });
  };

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const handleDateClick = (day: number) => {
    // Create date using the calendar's current month/year and clicked day
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth(); // 0-based month
    const clickedDate = new Date(year, month, day);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (clickedDate >= today) {
      setSelectedDate(clickedDate);
      
      // Format date as YYYY-MM-DD directly from components to avoid timezone issues
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      // Calculate RSVP deadline (day before event) using direct date manipulation
      // Create a new date for RSVP that's one day before the event
      let rsvpYear = year;
      let rsvpMonth = month + 1; // Convert to 1-based month for calculation
      let rsvpDay = day - 1;
      
      // Handle month boundary cases
      if (rsvpDay < 1) {
        rsvpMonth -= 1;
        if (rsvpMonth < 1) {
          rsvpMonth = 12;
          rsvpYear -= 1;
        }
        // Get the last day of the previous month
        const lastDayOfPrevMonth = new Date(rsvpYear, rsvpMonth - 1 + 1, 0).getDate();
        rsvpDay = lastDayOfPrevMonth;
      }
      
      const rsvpDateStr = `${rsvpYear}-${String(rsvpMonth).padStart(2, '0')}-${String(rsvpDay).padStart(2, '0')}`;
      

      
      setFormData(prev => ({
        ...prev,
        date: dateStr,
        rsvpDeadline: rsvpDateStr
      }));
      setShowCreateForm(true);
    }
  };

  const getEventsForDate = (day: number) => {
    // Format date as YYYY-MM-DD directly from calendar components to avoid timezone issues
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return events.filter(event => event.date === dateStr);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days: JSX.Element[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-24 bg-gray-50"></div>
      );
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const isToday = dayDate.toDateString() === today.toDateString();
      const isPast = dayDate < today;
      const dayEvents = getEventsForDate(day);

      days.push(
        <div
          key={day}
          className={`h-24 border border-gray-200 p-1 transition-colors ${
            isPast ? 'bg-gray-100' : 'bg-white hover:bg-ggaa-cream/50 cursor-pointer'
          } ${isToday ? 'bg-ggaa-green/10 border-ggaa-green' : ''}`}
          onClick={() => !isPast && handleDateClick(day)}
        >
          <div className={`text-sm font-medium ${isPast ? 'text-gray-400' : 'text-gray-900'} ${isToday ? 'text-ggaa-green' : ''}`}>
            {day}
          </div>
          <div className="mt-1 space-y-1">
            {dayEvents.slice(0, 2).map(event => (
              <div
                key={event.id}
                className="text-xs text-white px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80"
                style={{ backgroundColor: event.color || '#16a34a' }}
                onClick={(e) => {
                  e.stopPropagation();
                  openViewModal(event);
                }}
              >
                {event.title}
              </div>
            ))}
            {dayEvents.length > 2 && (
              <div
                className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDayEvents(dayEvents);
                  setShowAllEventsModal(true);
                }}
              >
                +{dayEvents.length - 2} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  // Get events for the currently displayed month (for mobile view)
  const getEventsForMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
    return events
      .filter(event => event.date.startsWith(monthStr))
      .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
  };

  // Check if a day has events (for mobile mini calendar dots)
  const dayHasEvents = (day: number) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.some(event => event.date === dateStr);
  };

  // Filter events for the Upcoming Events list: today through end of next month
  const getUpcomingEvents = () => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    const endStr = `${endOfNextMonth.getFullYear()}-${String(endOfNextMonth.getMonth() + 1).padStart(2, '0')}-${String(endOfNextMonth.getDate()).padStart(2, '0')}`;
    return events
      .filter(event => event.date >= todayStr && event.date <= endStr)
      .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
  };
  const upcomingEvents = getUpcomingEvents();

  return (
    <div className="px-4 py-6 md:px-12 md:py-10">
      <div className="mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1 md:mb-2">Event Management</h2>
        <p className="text-sm md:text-base text-gray-600 hidden md:block">
          Click an event to view details. Click a future date to create a new event.
        </p>
      </div>

      {/* Desktop Calendar - hidden on mobile */}
      <div className="hidden md:block bg-white rounded-xl shadow-lg p-6 mb-6">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ←
          </button>
          <h3 className="text-lg font-semibold text-gray-900">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <button
            onClick={() => navigateMonth('next')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            →
          </button>
        </div>

        {/* Days of week header */}
        <div className="grid grid-cols-7 gap-0 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="h-8 flex items-center justify-center text-sm font-medium text-gray-500 bg-gray-50">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-0 border border-gray-200">
          {renderCalendar()}
        </div>
      </div>

      {/* Mobile Calendar - compact mini calendar + create button */}
      <div className="md:hidden bg-white rounded-xl shadow-lg p-4 mb-4">
        {/* Month Navigation + Create Button */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ←
          </button>
          <h3 className="text-base font-semibold text-gray-900">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <button
            onClick={() => navigateMonth('next')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            →
          </button>
        </div>

        {/* Mini Calendar Grid */}
        <div className="grid grid-cols-7 gap-0 mb-3">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
            <div key={i} className="h-6 flex items-center justify-center text-xs font-medium text-gray-400">
              {day}
            </div>
          ))}
          {(() => {
            const daysInMonth = getDaysInMonth(currentDate);
            const firstDay = getFirstDayOfMonth(currentDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const cells: JSX.Element[] = [];
            for (let i = 0; i < firstDay; i++) {
              cells.push(<div key={`me-${i}`} className="h-8"></div>);
            }
            for (let day = 1; day <= daysInMonth; day++) {
              const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
              const isToday = dayDate.toDateString() === today.toDateString();
              const isPast = dayDate < today;
              const hasEvents = dayHasEvents(day);
              cells.push(
                <div
                  key={day}
                  className={`h-8 flex flex-col items-center justify-center ${!isPast ? 'cursor-pointer active:bg-gray-100 rounded' : ''}`}
                  onClick={() => !isPast && handleDateClick(day)}
                >
                  <span className={`text-xs font-medium leading-none w-7 h-7 flex items-center justify-center rounded-full ${
                    isToday ? 'bg-ggaa-green text-white' :
                    hasEvents && !isPast ? 'bg-green-100 text-green-800 font-semibold ring-2 ring-green-400' :
                    hasEvents && isPast ? 'bg-gray-200 text-gray-500' :
                    isPast ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {day}
                  </span>
                </div>
              );
            }
            return cells;
          })()}
        </div>

        <p className="text-xs text-gray-400 text-center mb-3">Tap an event to view details, or a future date to create one</p>

        {/* Mobile Month Events */}
        {(() => {
          const monthEvents = getEventsForMonth();
          if (monthEvents.length === 0) {
            return <p className="text-gray-400 text-center text-sm py-2">No events this month</p>;
          }
          return (
            <div className="space-y-2 border-t border-gray-100 pt-3">
              {monthEvents.map(event => (
                <div key={event.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100">
                  <div
                    className="w-1 self-stretch rounded-full flex-shrink-0"
                    style={{ backgroundColor: event.color || '#16a34a' }}
                  ></div>
                  <button
                    type="button"
                    onClick={() => openViewModal(event)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <span className="text-sm font-medium text-gray-900 truncate block">{event.title}</span>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {formatDateSafe(event.date)} {event.time !== 'TBD' ? `at ${event.time}` : '· Time TBD'}
                    </div>
                  </button>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => openViewModal(event)} className="text-xs text-ggaa-green">View</button>
                    <button onClick={() => openEditForm(event)} className="text-xs text-blue-600">Edit</button>
                    <button onClick={() => handleDeleteEvent(event.id)} className="text-xs text-red-600">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Create Event Modal */}
      {showCreateForm && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    📅 Create Event - {selectedDate.toLocaleDateString()}
                  </h3>
                  <p className="text-sm text-gray-600">Fill in the details to create a new event</p>
                </div>
                <button
                  onClick={closeCreateForm}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleCreateEvent} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                      placeholder="Enter event title"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Time {!timeTBD && <span className="text-red-500">*</span>}
                      </label>
                      <label htmlFor="createTimeTBD" className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="createTimeTBD"
                          checked={timeTBD}
                          onChange={(e) => {
                            setTimeTBD(e.target.checked);
                            if (e.target.checked) {
                              setFormData(prev => ({ ...prev, time: '' }));
                            }
                          }}
                          className="w-4 h-4 text-ggaa-green bg-gray-100 border-gray-300 rounded focus:ring-ggaa-green focus:ring-2"
                        />
                        <span className="ml-1.5 text-xs text-gray-500">TBD</span>
                      </label>
                    </div>
                    {!timeTBD ? (
                      <input
                        type="time"
                        required
                        value={formData.time}
                        onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                      />
                    ) : (
                      <div className="w-full p-3 border border-dashed border-gray-300 rounded-lg bg-gray-50 text-gray-400 text-sm italic">
                        To be determined
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    placeholder="Describe the event"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Location {!locationTBD && <span className="text-red-500">*</span>}
                      </label>
                      <label htmlFor="createLocationTBD" className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="createLocationTBD"
                          checked={locationTBD}
                          onChange={(e) => {
                            setLocationTBD(e.target.checked);
                            if (e.target.checked) {
                              setFormData(prev => ({ ...prev, location: '' }));
                            }
                          }}
                          className="w-4 h-4 text-ggaa-green bg-gray-100 border-gray-300 rounded focus:ring-ggaa-green focus:ring-2"
                        />
                        <span className="ml-1.5 text-xs text-gray-500">TBD</span>
                      </label>
                    </div>
                    {!locationTBD ? (
                      <input
                        type="text"
                        required
                        value={formData.location}
                        onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                        placeholder="Event location"
                      />
                    ) : (
                      <div className="w-full p-3 border border-dashed border-gray-300 rounded-lg bg-gray-50 text-gray-400 text-sm italic">
                        To be determined
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Attendees <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={formData.maxAttendees}
                      onChange={(e) => setFormData(prev => ({ ...prev, maxAttendees: parseInt(e.target.value) }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">Choose the color for this event on the calendar</span>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="rsvpEnabled"
                    checked={formData.rsvpEnabled}
                    onChange={(e) => setFormData(prev => ({ ...prev, rsvpEnabled: e.target.checked }))}
                    className="w-4 h-4 text-ggaa-green bg-gray-100 border-gray-300 rounded focus:ring-ggaa-green focus:ring-2"
                  />
                  <label htmlFor="rsvpEnabled" className="text-sm font-medium text-gray-700">
                    Enable RSVP for this event
                  </label>
                </div>

                {formData.rsvpEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      RSVP Deadline <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.rsvpDeadline}
                      onChange={(e) => setFormData(prev => ({ ...prev, rsvpDeadline: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    />
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
                  <button
                    type="button"
                    onClick={closeCreateForm}
                    className="px-6 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 transition-colors"
                  >
                    Create Event
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditForm && editingEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    ✏️ Edit Event - {editingEvent.title}
                  </h3>
                  <p className="text-sm text-gray-600">Update the event details</p>
                </div>
                <button
                  onClick={closeEditForm}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleUpdateEvent} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                      placeholder="Enter event title"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Time {!timeTBD && <span className="text-red-500">*</span>}
                      </label>
                      <label htmlFor="editTimeTBD" className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="editTimeTBD"
                          checked={timeTBD}
                          onChange={(e) => {
                            setTimeTBD(e.target.checked);
                            if (e.target.checked) {
                              setFormData(prev => ({ ...prev, time: '' }));
                            }
                          }}
                          className="w-4 h-4 text-ggaa-green bg-gray-100 border-gray-300 rounded focus:ring-ggaa-green focus:ring-2"
                        />
                        <span className="ml-1.5 text-xs text-gray-500">TBD</span>
                      </label>
                    </div>
                    {!timeTBD ? (
                      <input
                        type="time"
                        required
                        value={formData.time}
                        onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                      />
                    ) : (
                      <div className="w-full p-3 border border-dashed border-gray-300 rounded-lg bg-gray-50 text-gray-400 text-sm italic">
                        To be determined
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    placeholder="Describe the event"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.date}
                      onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Location {!locationTBD && <span className="text-red-500">*</span>}
                      </label>
                      <label htmlFor="editLocationTBD" className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="editLocationTBD"
                          checked={locationTBD}
                          onChange={(e) => {
                            setLocationTBD(e.target.checked);
                            if (e.target.checked) {
                              setFormData(prev => ({ ...prev, location: '' }));
                            }
                          }}
                          className="w-4 h-4 text-ggaa-green bg-gray-100 border-gray-300 rounded focus:ring-ggaa-green focus:ring-2"
                        />
                        <span className="ml-1.5 text-xs text-gray-500">TBD</span>
                      </label>
                    </div>
                    {!locationTBD ? (
                      <input
                        type="text"
                        required
                        value={formData.location}
                        onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                        placeholder="Event location"
                      />
                    ) : (
                      <div className="w-full p-3 border border-dashed border-gray-300 rounded-lg bg-gray-50 text-gray-400 text-sm italic">
                        To be determined
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Attendees <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={formData.maxAttendees}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxAttendees: parseInt(e.target.value) }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">Choose the color for this event on the calendar</span>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="editRsvpEnabled"
                    checked={formData.rsvpEnabled}
                    onChange={(e) => setFormData(prev => ({ ...prev, rsvpEnabled: e.target.checked }))}
                    className="w-4 h-4 text-ggaa-green bg-gray-100 border-gray-300 rounded focus:ring-ggaa-green focus:ring-2"
                  />
                  <label htmlFor="editRsvpEnabled" className="text-sm font-medium text-gray-700">
                    Enable RSVP for this event
                  </label>
                </div>

                {formData.rsvpEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      RSVP Deadline <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.rsvpDeadline}
                      onChange={(e) => setFormData(prev => ({ ...prev, rsvpDeadline: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
                    />
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
                  <button
                    type="button"
                    onClick={closeEditForm}
                    className="px-6 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 transition-colors"
                  >
                    Update Event
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Events List - desktop only (mobile shows events in mini calendar above) */}
      <div className="hidden md:block bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Events</h3>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ggaa-green"></div>
            <span className="ml-2 text-gray-600">Loading events...</span>
          </div>
        ) : upcomingEvents.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No upcoming events</p>
        ) : (
          <div className="space-y-3">
            {upcomingEvents.map(event => (
              <div key={event.id} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex justify-between items-start">
                  <button
                    type="button"
                    onClick={() => openViewModal(event)}
                    className="text-left flex-1 mr-4"
                  >
                    <h4 className="font-medium text-gray-900 hover:text-ggaa-green">{event.title}</h4>
                    <p className="text-sm text-gray-600 line-clamp-2">{linkify(event.description)}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      📅 {formatDateSafe(event.date)} {event.time !== 'TBD' ? `at ${event.time}` : '· Time TBD'} • 📍 {event.location}
                    </p>
                    <p className="text-sm text-gray-500">
                      👥 {event.currentAttendees}/{event.maxAttendees} attendees
                    </p>
                    {event.rsvpEnabled && (
                      <p className="text-sm text-ggaa-green">✓ RSVP Enabled</p>
                    )}
                  </button>
                  <div className="flex space-x-2 flex-shrink-0">
                    <button
                      onClick={() => openViewModal(event)}
                      className="text-ggaa-green hover:underline text-sm"
                    >
                      View
                    </button>
                    <button 
                      onClick={() => {
                        setViewingRSVPEvent(event);
                        setShowRSVPModal(true);
                      }}
                      className="text-ggaa-green hover:underline text-sm"
                    >
                      View RSVPs ({event.rsvps?.filter((r: any) => r.status === 'attending').length || 0})
                    </button>
                    <button 
                      onClick={() => openEditForm(event)}
                      className="text-blue-600 hover:underline text-sm"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteEvent(event.id)}
                      className="text-red-600 hover:underline text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Event Details Modal */}
      {showViewModal && viewingEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start gap-3">
                  <div
                    className="w-4 h-4 rounded mt-1 flex-shrink-0"
                    style={{ backgroundColor: viewingEvent.color || '#16a34a' }}
                  />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{viewingEvent.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      📅 {formatDateSafe(viewingEvent.date)} {viewingEvent.time !== 'TBD' ? `at ${viewingEvent.time}` : '· Time TBD'}
                    </p>
                    <p className="text-sm text-gray-600">📍 {viewingEvent.location}</p>
                  </div>
                </div>
                <button
                  onClick={closeViewModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Details</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-800 whitespace-pre-wrap">
                  {linkify(viewingEvent.description)}
                </div>
              </div>

              {viewingEvent.rsvpEnabled && (
                <p className="text-sm text-gray-600 mb-4">
                  👥 {viewingEvent.currentAttendees}/{viewingEvent.maxAttendees} attendees · RSVP enabled
                </p>
              )}

              <div className="flex flex-wrap gap-2 justify-end">
                {viewingEvent.rsvpEnabled && (
                  <button
                    onClick={() => {
                      closeViewModal();
                      setViewingRSVPEvent(viewingEvent);
                      setShowRSVPModal(true);
                    }}
                    className="px-4 py-2 text-ggaa-green border border-ggaa-green rounded-lg hover:bg-green-50 text-sm"
                  >
                    View RSVPs
                  </button>
                )}
                <button
                  onClick={() => openEditForm(viewingEvent)}
                  className="px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    closeViewModal();
                    handleDeleteEvent(viewingEvent.id);
                  }}
                  className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 text-sm"
                >
                  Delete
                </button>
                <button
                  onClick={closeViewModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Events for Day Modal */}
      {showAllEventsModal && selectedDayEvents.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Events on {formatDateSafe(selectedDayEvents[0].date)}
                </h3>
                <button
                  onClick={() => {
                    setShowAllEventsModal(false);
                    setSelectedDayEvents([]);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-3">
                {selectedDayEvents.map(event => (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => {
                      setShowAllEventsModal(false);
                      setSelectedDayEvents([]);
                      openViewModal(event);
                    }}
                    className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: event.color || '#16a34a' }}
                      />
                      <span className="font-medium text-gray-900">{event.title}</span>
                    </div>
                    <p className="text-sm text-gray-500">
                      {event.time !== 'TBD' ? event.time : 'Time TBD'} · {event.location}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RSVP Viewer Modal */}
      {showRSVPModal && viewingRSVPEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">RSVPs for {viewingRSVPEvent.title}</h3>
                  <p className="text-sm text-gray-600">
                    📅 {formatDateSafe(viewingRSVPEvent.date)} at {viewingRSVPEvent.time}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowRSVPModal(false);
                    setViewingRSVPEvent(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="text-2xl font-bold text-green-600">
                    {viewingRSVPEvent.rsvps?.filter((r: any) => r.status === 'attending').length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Attending Families</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">{viewingRSVPEvent.currentAttendees}</div>
                  <div className="text-sm text-gray-600">Total Attendees</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-gray-600">
                    {viewingRSVPEvent.rsvps?.filter((r: any) => r.status === 'not_attending').length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Not Attending</div>
                </div>
              </div>

              {/* Attending Families */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">✅ Attending Families</h4>
                <div className="space-y-4">
                  {viewingRSVPEvent.rsvps
                    ?.filter((rsvp: any) => rsvp.status === 'attending')
                    .map((rsvp: any, index: number) => (
                      <div key={index} className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-medium text-gray-900">{rsvp.familyName || 'Unknown Family'}</div>
                          <div className="text-sm text-gray-600">
                            {rsvp.attendeeCount} {rsvp.attendeeCount === 1 ? 'attendee' : 'attendees'}
                          </div>
                        </div>
                        {rsvp.attendees && rsvp.attendees.length > 0 ? (
                          <div className="mt-2">
                            <div className="text-sm font-medium text-gray-700 mb-1">Attendees:</div>
                            <div className="flex flex-wrap gap-2">
                              {rsvp.attendees.map((attendee: any, aIndex: number) => (
                                <span
                                  key={aIndex}
                                  className="inline-flex items-center px-3 py-1 bg-white rounded-full text-sm text-gray-700 border border-green-200"
                                >
                                  👤 {attendee.firstName} {attendee.lastName}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 italic">No attendee names provided</div>
                        )}
                        <div className="text-xs text-gray-500 mt-2">
                          Responded: {new Date(rsvp.respondedAt).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  {!viewingRSVPEvent.rsvps?.some((r: any) => r.status === 'attending') && (
                    <div className="text-center py-8 text-gray-500">No families attending yet</div>
                  )}
                </div>
              </div>

              {/* Not Attending Families */}
              {viewingRSVPEvent.rsvps?.some((r: any) => r.status === 'not_attending') && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">❌ Not Attending</h4>
                  <div className="space-y-2">
                    {viewingRSVPEvent.rsvps
                      ?.filter((rsvp: any) => rsvp.status === 'not_attending')
                      .map((rsvp: any, index: number) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex justify-between items-center">
                          <span className="text-gray-700">{rsvp.familyName || 'Unknown Family'}</span>
                          <span className="text-xs text-gray-500">
                            {new Date(rsvp.respondedAt).toLocaleString()}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowRSVPModal(false);
                    setViewingRSVPEvent(null);
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 